export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const toPercent = (value: number): number => {
  if (!isFinite(value)) return 0;
  return value;
};

export const calculateQuotaUsagePercent = (
  used: number,
  total: number,
): number => {
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.min(100, Math.max(0, (used / total) * 100));
};

export const formatQuotaUsagePercent = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return "0%";
  if (value >= 100) return "100%";

  // Do not round an incomplete quota up to 100%.
  const truncatedValue = Math.floor(value * 100) / 100;
  return `${truncatedValue
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1")}%`;
};

// Same as calculateQuotaUsagePercent/formatQuotaUsagePercent, uncapped - for
// cards that should show real over-budget values like "107%" instead of
// capping at 100%. Leaves the capped pair alone; their other callers need the
// cap for progress-bar widths.
export const calculateRawUsagePercent = (
  used: number,
  total: number,
): number => {
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, (used / total) * 100);
};

export const formatRawUsagePercent = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return "0%";
  const truncatedValue = Math.floor(value * 100) / 100;
  return `${truncatedValue
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1")}%`;
};

/**
 * Formats a percentage value with a dynamic number of decimals so small
 * values stay readable (e.g. 0.0212%) while large values stay compact (56%).
 */
export const formatPercent = (value: number): string => {
  if (!isFinite(value)) return "0%";
  const abs = Math.abs(value);
  let digits: number;
  if (abs === 0) digits = 0;
  else if (abs >= 10) digits = 0;
  else if (abs >= 1) digits = 1;
  else if (abs >= 0.1) digits = 2;
  else digits = 4;
  return `${value.toFixed(digits)}%`;
};

/** Convenience: percent value → formatted percent string. */
export const formatSpendPercent = (value: number): string =>
  formatPercent(toPercent(value));

export const formatCompactNumber = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
};

export const formatPlanLimit = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);

export const formatPlanCurrencyLimit = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat("en-US").format(value);
};

export const formatTokens = formatCompactNumber;

export const categoryColors: Record<string, string> = {
  apis: "#8b5cf6",
  models: "#f87171",
  flows: "#14b8a6",
  rags: "#f59e0b",
  deployments: "#3b82f6",
};

export const categoryLabels: Record<string, string> = {
  apis: "APIs",
  models: "Models",
  flows: "Flows",
  rags: "RAGs",
  deployments: "Deployments",
};
